import React, { useState } from 'react'
import styled from 'styled-components'
import CreationToolGradient from '../../site/creation-tools/types/gradient'
import Observer from 'shared/components/observer'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import { highestZIndex } from 'shared/css/z-indexes'
import { GradientButton } from '../../site/creation-tools/components/buttons'
import { giphyLightGrey, giphyWhite } from '@giphy/js-brand'
import { mobile } from 'shared/util/media-query'
import { TrendingIcon } from 'shared/components/homepage-icons'
import { fetchDashboardTrendingSearches } from 'shared/api/index'
import { googleAnalyticsEvent } from 'shared/util/analytics'

const TrendingSearchesContainer = styled.div`
    margin-top: 20px;
    margin-bottom: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: linear-gradient(-180deg, rgb(33, 33, 33) 0%, rgb(24, 24, 24) 98%);
    color: ${giphyLightGrey};
    border-radius: 4px;
    flex-flow: row wrap;
    padding-top: 40px;
    padding-bottom: 40px;
`

const Title = styled.div`
    font-weight: bold;
    font-size: 26px;
    font-family: NexaBlack;
    letter-spacing: 0px;
    color: ${giphyWhite};
`

const Info = styled.div`
    font-size: 16px;
    font-weight: bold;
    letter-spacing: 0px;
    line-height: 22px;
    margin-top: 8px;
    & a {
        color: ${giphyLightGrey};
        text-decoration: underline;
    }
`

const EditorialCalendarButton = styled(GradientButton)`
    border-radius: 2px;
    height: 36px;
    width: 220px;
    margin-top: 10px;
    ${mobile.css`
        margin-left: auto;
        margin-right: auto;
    `}
    z-index: ${highestZIndex};
`

const TagsContainer = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
`

const Tag = styled.div`
    color: ${giphyWhite};
    max-width: 211px;
    height: 42px;
    background: linear-gradient(-180deg, rgb(40, 40, 40) 0%, rgb(33, 33, 32) 100%);
    border-radius: 25px;
    font-size: 17px;
    font-family: InterFace;
    font-weight: bold;
    letter-spacing: 0px;
    line-height: 42px;
    text-align: center;
    & svg {
        height: 14px;
    }
    cursor: pointer;
    user-select: none;
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-left: 10px;
    padding-right: 18px;
    margin-top: 10px;
    justify-self: center;
`

type TrendingSearch = {
    url: string
    query: string
}

type TrendingSearches = {
    data: TrendingSearch[]
}

const DashboardTrendingSearches = () => {
    const [trendingData, setTrendingData] = useState<TrendingSearches>()

    useAsyncEffect(async () => {
        const response = await fetchDashboardTrendingSearches()
        setTrendingData(response)
    }, [])

    const uploadingLink = (
        <a
            href="/upload"
            target="_blank"
            onClick={() => googleAnalyticsEvent({
                category: 'dashboard',
                action: 'Trending Searches - Uploading Link',
                label: null,
            })
        }>
            Uploading
        </a>
    )

    const taggingLink = (
        <a
            href="https://support.giphy.com/hc/en-us/articles/360026405951-How-to-Tag-Your-Content"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => googleAnalyticsEvent({
                category: 'dashboard',
                action: 'Trending Searches - Tagging Link',
                label: null,
            })
        }>
            Tagging
        </a>
    )

    const trendingContainerVisible = (d: TrendingSearches) => {
        googleAnalyticsEvent({
            category: 'dashboard',
            action: 'Trending Searches - All Tag Names',
            label: d.data.map(({ query }) => query ?? "").join(", "),
        })
    }

    if (trendingData) {
        return (
            <div>
                <TrendingSearchesContainer>
                    <Title>Trending on GIPHY 🔥</Title>
                    <Info>
                        Maximize your impact by {uploadingLink} and {taggingLink} content for these trending search terms!
                    </Info>
                    <Observer onVisible={() => trendingContainerVisible(trendingData)}>
                        <TagsContainer>
                            {trendingData.data.map((item) => {
                                return (
                                    <Tag key={item.url}
                                        {...{
                                            as: 'a',
                                            href: item.url,
                                            target: '_blank',
                                            rel: 'noopener noreferrer',
                                            onClick: () => googleAnalyticsEvent({
                                                category: 'dashboard',
                                                action: 'Trending Searches - Search Term Link',
                                                label: item.query,
                                            }),
                                        }}
                                    >
                                        <TrendingIcon /> {item.query}
                                    </Tag>
                                )
                            })}
                        </TagsContainer>
                    </Observer>
                    <Info>
                        Want more inspiration? Plan ahead for high traffic moments throughout the year.
                    </Info>
                    <EditorialCalendarButton
                        {...{
                            as: 'a',
                            href: 'https://support.giphy.com/hc/en-us/articles/360059071691-2022-Content-Calendar',
                            colors: CreationToolGradient.colors(CreationToolGradient.IndigoPurple),
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            onClick: () => googleAnalyticsEvent({
                                category: 'dashboard',
                                action: 'Trending Searches - Content Calendar Button',
                                label: null,
                            }),
                        }}
                    >
                        GIPHY Content Calendar
                    </EditorialCalendarButton>
                </TrendingSearchesContainer>
            </div>
        )
    }
    return (<div></div>)
}

export default DashboardTrendingSearches
